/**
 * Centered hyperspace-jump indicator. Shows a 3-second countdown while the
 * jump is charging, a steady banner during the jump itself, and a brief
 * error toast (no destination, not facing target, etc.) when the player
 * tries to start a jump that can't begin.
 */
export class JumpHud {
  readonly element: HTMLElement;

  private readonly bigEl: HTMLElement;
  private readonly subEl: HTMLElement;
  private errorTimer: number | null = null;

  constructor() {
    this.element = document.createElement('div');
    this.element.className = 'jump-hud';

    this.bigEl = document.createElement('div');
    this.bigEl.className = 'jump-hud-big';

    this.subEl = document.createElement('div');
    this.subEl.className = 'jump-hud-sub';

    this.element.append(this.bigEl, this.subEl);
  }

  showCountdown(secondsRemaining: number): void {
    this.clearErrorTimer();
    this.element.classList.remove('is-jumping', 'is-error');
    this.element.classList.add('is-visible', 'is-countdown');
    this.bigEl.textContent = String(Math.max(1, Math.ceil(secondsRemaining)));
    this.subEl.textContent = 'CHARGING JUMP';
  }

  showJumping(distance: number): void {
    this.clearErrorTimer();
    this.element.classList.remove('is-countdown', 'is-error');
    this.element.classList.add('is-visible', 'is-jumping');
    this.bigEl.textContent = 'JUMP';
    this.subEl.textContent = formatDistance(distance);
  }

  showError(message: string, durationMs = 1600): void {
    this.clearErrorTimer();
    this.element.classList.remove('is-countdown', 'is-jumping');
    this.element.classList.add('is-visible', 'is-error');
    this.bigEl.textContent = '';
    this.subEl.textContent = message;
    this.errorTimer = window.setTimeout(() => {
      this.errorTimer = null;
      if (this.element.classList.contains('is-error')) this.hide();
    }, durationMs);
  }

  hide(): void {
    this.clearErrorTimer();
    this.element.classList.remove(
      'is-visible',
      'is-countdown',
      'is-jumping',
      'is-error'
    );
  }

  private clearErrorTimer(): void {
    if (this.errorTimer !== null) {
      window.clearTimeout(this.errorTimer);
      this.errorTimer = null;
    }
  }
}

function formatDistance(distance: number): string {
  if (distance < 1000) return `${Math.round(distance)} u`;
  return `${(distance / 1000).toFixed(2)} Ls`;
}
